import { useCallback, useEffect, useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { formatBCH, formatFiat } from '@/lib/bch';
import { buildReceiptUrl, ReceiptData } from '@/lib/receipt';
import { markTransactionRefunded } from '@/lib/posStorage';
import { loadCashStamps, isStampActive } from '@/lib/cashStampStorage';
import { PUSD } from '@/lib/stablecoin';
import { QRCodeCanvas } from '@/components/ui/qrcode';

export function SuccessView() {
  const { lastTransaction, settings, setView, setFiatAmount, setPendingBCH, clearCart, setTipAmount, setNote, setDiscount } = usePOS();
  const [shared, setShared] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);

  const handleNewSale = useCallback(() => {
    setFiatAmount(0);
    setPendingBCH(0);
    setTipAmount(0);
    setDiscount(0, '');
    setNote('');
    clearCart();
    setView('checkout');
  }, [setFiatAmount, setPendingBCH, setTipAmount, setDiscount, setNote, clearCart, setView]);

  // Void the last transaction — mark refunded then reset
  const handleVoid = useCallback(() => {
    if (lastTransaction) {
      markTransactionRefunded(lastTransaction.id, 'Voided at point of sale');
    }
    handleNewSale();
  }, [lastTransaction, handleNewSale]);

  // Auto-reset after 20 seconds — longer now so customer has time to scan
  useEffect(() => {
    const timer = setTimeout(handleNewSale, 20000);
    return () => clearTimeout(timer);
  }, [handleNewSale]);

  const receiptUrl = lastTransaction
    ? buildReceiptUrl({
        v: 1,
        merchantName: settings.merchantName,
        merchantTagline: settings.merchantTagline,
        logoDataUrl: settings.logoDataUrl,
        brandColor: settings.brandColor,
        currency: lastTransaction.currency,
        subtotal: lastTransaction.subtotal,
        discountAmount: lastTransaction.discountAmount,
        discountLabel: lastTransaction.discountLabel,
        taxAmount: lastTransaction.taxAmount,
        taxRate: lastTransaction.taxRate,
        taxName: lastTransaction.taxName,
        tipAmount: lastTransaction.tipAmount,
        fiatAmount: lastTransaction.fiatAmount,
        bchAmount: lastTransaction.bchAmount,
        asset: lastTransaction.asset,
        tokenAmount: lastTransaction.tokenAmount,
        tokenSymbol: lastTransaction.tokenSymbol,
        note: lastTransaction.note,
        txid: lastTransaction.txid,
        address: lastTransaction.address,
        timestamp: lastTransaction.timestamp,
        items: lastTransaction.items,
      } satisfies ReceiptData)
    : null;

  const handlePrint = () => {
    if (receiptUrl) window.open(receiptUrl, '_blank');
  };

  const handleShare = async () => {
    if (!receiptUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Receipt from ${settings.merchantName || 'BCHPAY.Cash'}`,
          text: `Your payment receipt from ${settings.merchantName || 'BCHPAY.Cash'}`,
          url: receiptUrl,
        });
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      } catch {
        await copyToClipboard(receiptUrl);
      }
    } else {
      await copyToClipboard(receiptUrl);
    }
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch {
      prompt('Copy receipt link:', url);
    }
  };

  const qrSize = Math.min(window.innerWidth - 96, 200);

  return (
    <div className="flex flex-col min-h-full items-center gap-4 px-4 pb-2">

      {/* Branded header */}
      <div className="w-full rounded-3xl overflow-hidden shrink-0">
        <div className="px-6 py-5 flex flex-col items-center gap-2 text-center" style={{ backgroundColor: settings.brandColor ?? '#0AC18E' }}>
          {settings.logoDataUrl ? (
            <img src={settings.logoDataUrl} alt={settings.merchantName} className="h-10 max-w-[140px] object-contain mb-1" />
          ) : (
            <p className="text-base font-black text-white">{settings.merchantName || 'BCHPAY.Cash'}</p>
          )}
          {settings.merchantTagline && (
            <p className="text-xs text-white/70">{settings.merchantTagline}</p>
          )}
          {/* PAID badge */}
          <div className="flex items-center gap-3 mt-1">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-4xl font-black text-white tracking-tight">PAID</h1>
          </div>
          {lastTransaction && (
            <p className="text-2xl font-bold text-white/90">
              {formatFiat(lastTransaction.fiatAmount, settings.currency)}
            </p>
          )}
        </div>
      </div>

      {/* Receipt QR — the star of the show */}
      {receiptUrl && (
        <div className="w-full shrink-0 bg-white dark:bg-zinc-800 rounded-3xl border border-zinc-100 dark:border-zinc-700 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 border-b border-[#0AC18E]/20 px-4 py-2.5 flex items-center gap-2">
            <span className="text-base">🧾</span>
            <p className="text-xs font-bold text-[#0AC18E] uppercase tracking-wider">
              Customer Receipt
            </p>
            <p className="text-xs text-zinc-400 ml-auto">Scan to get receipt</p>
          </div>
          {/* QR */}
          <div className="flex flex-col items-center py-4 px-4 gap-2">
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-zinc-100">
              <QRCodeCanvas
                value={receiptUrl}
                size={qrSize}
                level="M"
                className="rounded-lg"
              />
            </div>
            <p className="text-xs text-zinc-400 text-center">
              Customer scans this with their phone camera
            </p>
          </div>
        </div>
      )}

      {/* Transaction summary — compact */}
      {lastTransaction && (
        <div className="w-full bg-white dark:bg-zinc-800 rounded-2xl px-4 py-3 border border-zinc-100 dark:border-zinc-700 shadow-sm space-y-1">
          {/* Line items */}
          {lastTransaction.items && lastTransaction.items.length > 0 && (
            <div className="space-y-1 pb-2 mb-1 border-b border-dashed border-zinc-100 dark:border-zinc-700">
              {lastTransaction.items.map((item, i) => (
                <div key={i} className="flex justify-between text-xs text-zinc-500 dark:text-zinc-400">
                  <span>{item.qty}× {item.name}</span>
                  <span className="font-medium">{formatFiat(item.price * item.qty, lastTransaction.currency)}</span>
                </div>
              ))}
            </div>
          )}
          {lastTransaction.discountAmount > 0 && (
            <div className="flex justify-between text-xs text-red-500 font-semibold">
              <span>Discount {lastTransaction.discountLabel ? `(${lastTransaction.discountLabel})` : ''}</span>
              <span>−{formatFiat(lastTransaction.discountAmount, lastTransaction.currency)}</span>
            </div>
          )}
          {lastTransaction.taxAmount > 0 && (
            <div className="flex justify-between text-xs text-zinc-400">
              <span>{lastTransaction.taxName} ({lastTransaction.taxRate}%)</span>
              <span>{formatFiat(lastTransaction.taxAmount, lastTransaction.currency)}</span>
            </div>
          )}
          {lastTransaction.tipAmount > 0 && (
            <div className="flex justify-between text-xs text-[#0AC18E] font-semibold">
              <span>Tip</span>
              <span>{formatFiat(lastTransaction.tipAmount, lastTransaction.currency)}</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-1 border-t border-zinc-100 dark:border-zinc-700">
            {lastTransaction.txid.startsWith('offline-') ? (
              <span className="text-xs font-medium text-amber-500" title="Confirmed offline — no blockchain transaction ID">
                offline
              </span>
            ) : (
              <span className="text-xs font-mono text-zinc-300 dark:text-zinc-600 truncate max-w-[120px]" title={lastTransaction.txid}>
                {lastTransaction.txid.slice(0, 8)}…
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 text-sm font-mono font-medium text-[#0AC18E]">
              {lastTransaction.asset === 'PUSD' && lastTransaction.tokenAmount != null ? (
                <>
                  {PUSD.iconUrl && <img src={PUSD.iconUrl} alt="" className="h-4 w-4 rounded-full" />}
                  {`${lastTransaction.tokenAmount.toFixed(2)} ${lastTransaction.tokenSymbol ?? 'PUSD'}`}
                </>
              ) : (
                `${formatBCH(lastTransaction.bchAmount)} BCH`
              )}
            </span>
          </div>
        </div>
      )}

      {/* Receipt actions + New Sale */}
      <div className="w-full flex gap-2">
        {receiptUrl && (
          <>
            <button
              onClick={handlePrint}
              title="Open receipt to print"
              className="flex items-center justify-center gap-1.5 px-3.5 py-3.5 rounded-2xl text-sm font-bold bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-200 border-2 border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/50 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <button
              onClick={handleShare}
              className={`flex items-center justify-center gap-1.5 px-3.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-200 ${
                shared
                  ? 'bg-[#0AC18E]/20 text-[#0AC18E] border-2 border-[#0AC18E]'
                  : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-200 border-2 border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {shared ? '✓' : 'Share'}
            </button>
          </>
        )}
        <button
          onClick={handleNewSale}
          className="flex-1 py-3.5 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 active:scale-98 hover:bg-[#09AF81] transition-all"
        >
          New Sale
        </button>
      </div>

      {/* CashStamp promo prompt — shown if merchant has active stamps */}
      {(() => {
        const activeStamps = loadCashStamps().filter(isStampActive);
        if (activeStamps.length === 0) return null;
        return (
          <div className="w-full bg-amber-50 dark:bg-amber-950/20 rounded-2xl px-4 py-3 border border-amber-200 dark:border-amber-800/40 flex items-center gap-3">
            <span className="text-xl shrink-0">🎁</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-amber-700 dark:text-amber-300">Share a CashStamp!</p>
              <p className="text-[11px] text-amber-600/80 dark:text-amber-500/80 leading-snug">
                Give this customer a promo link for free BCH
              </p>
            </div>
            <button
              onClick={() => setView('cashstamps')}
              className="text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-3 py-1.5 rounded-xl border border-amber-200 dark:border-amber-700/50 hover:bg-amber-200 dark:hover:bg-amber-800/40 transition-colors shrink-0"
            >
              View →
            </button>
          </div>
        );
      })()}

      {/* Void sale — small, below the main actions */}
      <button
        onClick={() => setShowVoidConfirm(true)}
        className="text-xs text-red-400 hover:text-red-600 transition-colors pb-1"
      >
        Void this sale
      </button>

      {/* Void confirm dialog */}
      {showVoidConfirm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl p-5 pb-safe space-y-4">
            <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto" />
            <div>
              <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-50">Void this sale?</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                This marks the sale as refunded in your records. It does <span className="font-semibold text-zinc-600 dark:text-zinc-300">not</span> move any BCH — you'll need to return funds manually from your wallet if payment was already received.
              </p>
            </div>
            {lastTransaction && (
              <div className="bg-zinc-50 dark:bg-zinc-800 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-700 flex justify-between text-sm">
                <span className="text-zinc-500">Amount</span>
                <span className="font-bold text-zinc-800 dark:text-zinc-100">{formatFiat(lastTransaction.fiatAmount, lastTransaction.currency)}</span>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowVoidConfirm(false)}
                className="flex-1 py-4 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
              >
                Keep Sale
              </button>
              <button
                onClick={handleVoid}
                className="flex-[2] py-4 rounded-2xl text-sm font-bold bg-red-500 text-white shadow-lg shadow-red-500/30 hover:bg-red-600 active:scale-98 transition-all"
              >
                Void Sale
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
